import { useState } from "react";
import { PenLine, Send, Check, X } from "lucide-react";
import { nip19 } from "nostr-tools";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useToast } from "@/hooks/useToast";
import { getKindInfo, storageClass } from "@/lib/nostrKinds";
import type { DraftEvent } from "@/hooks/useKindlessAgent";

interface DraftCardProps {
  draft: DraftEvent;
}

export function DraftCard({ draft }: DraftCardProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const { toast } = useToast();
  const [status, setStatus] = useState<"idle" | "signed" | "cancelled">("idle");
  const [publishedId, setPublishedId] = useState<string | null>(null);

  const info = getKindInfo(draft.kind);

  const handleSign = async () => {
    if (!user) {
      toast({
        title: "Not signed in",
        description: "Use the Join button to sign in before signing events.",
        variant: "destructive",
      });
      return;
    }
    try {
      const event = await publish({ kind: draft.kind, content: draft.content, tags: draft.tags });
      setStatus("signed");
      setPublishedId(event.id);
      toast({ title: "Signed & published", description: `kind ${draft.kind} event broadcast.` });
    } catch (e) {
      toast({
        title: "Failed to publish",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="border-primary/40 bg-primary/[0.03]">
      <CardHeader className="gap-2 pb-3">
        <div className="flex items-center gap-2">
          <PenLine className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Draft event — review before signing</span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary" className="font-mono text-[10px]">
            kind {draft.kind}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {info.name}
          </Badge>
          <Badge variant="outline" className="text-[10px] text-muted-foreground">
            {storageClass(draft.kind)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {draft.content ? (
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">content</div>
            <p className="whitespace-pre-wrap break-words rounded-lg bg-muted/50 p-2.5 text-sm">
              {draft.content}
            </p>
          </div>
        ) : (
          <p className="text-xs italic text-muted-foreground">(empty content)</p>
        )}

        {draft.tags.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">tags</div>
            <div className="space-y-1">
              {draft.tags.map((tag, i) => (
                <div key={i} className="flex gap-2 text-xs font-mono">
                  <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-primary font-semibold">
                    {tag[0]}
                  </span>
                  <span className="break-all text-muted-foreground">{tag.slice(1).join(" · ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {status === "signed" && publishedId ? (
          <div className="flex items-center gap-2 rounded-lg bg-green-500/10 p-2.5 text-sm text-green-700 dark:text-green-400">
            <Check className="h-4 w-4 shrink-0" />
            <a
              href={`https://njump.me/${nip19.neventEncode({ id: publishedId, author: user?.pubkey })}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate font-mono text-xs hover:underline"
            >
              Published — view event
            </a>
          </div>
        ) : status === "cancelled" ? (
          <p className="text-sm text-muted-foreground">Draft discarded.</p>
        ) : (
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={handleSign} disabled={isPending} className="gap-1.5">
              <Send className="h-3.5 w-3.5" />
              {isPending ? "Signing…" : "Sign & publish"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setStatus("cancelled")}
              disabled={isPending}
              className="gap-1.5 text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
              Discard
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
